import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useBookDemo } from "@/contexts/BookDemoContext";
import websiteLogo from "@/assets/website_logo.png";
import websiteLogoWhite from "@/assets/website_logo_white.png";

const LINKS = [
  { label: "Product", href: "#benefits" },
  { label: "How it works", href: "#how-it-works" },
  { label: "Role Plays", href: "#role-plays" },
  { label: "Analytics", href: "#analytics" },
  { label: "Impact", href: "#impact" },
  { label: "FAQ", href: "#faq" },
];

export default function Navbar() {
  const { openBookDemo } = useBookDemo();
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Prevent background page scroll while mobile menu is open (cleaner on iOS/Android)
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  useEffect(() => {
    let lastY = window.scrollY;

    const onScroll = () => {
      const y = window.scrollY;
      const delta = y - lastY;

      setScrolled(y > 12);

      // Always show near top; hide on scroll-down; show on scroll-up
      if (y < 80) {
        setHidden(false);
      } else if (delta > 6) {
        setHidden(true);
      } else if (delta < -6) {
        setHidden(false);
      }

      lastY = y;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Keep nav visible when mobile menu is open
  const effectivelyHidden = hidden && !mobileOpen;

  // Solid light chrome when scrolled OR mobile menu open (hero stays readable)
  const solidNavChrome = scrolled || mobileOpen;

  // When transparent over Hero → white nav text ; with solid chrome → slate text.
  const onDark = !solidNavChrome;

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        solidNavChrome
          ? "bg-white/95 backdrop-blur-xl border-b border-slate-200/70 shadow-[0_6px_24px_-16px_rgba(15,23,42,0.15)]"
          : "bg-transparent"
      }`}
      style={{
        transform: effectivelyHidden ? "translateY(-100%)" : "translateY(0)",
        willChange: "transform",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <a
            href="#top"
            className="flex items-center gap-2 group shrink-0 min-w-0"
            data-testid="nav-logo"
          >
            <img
              src={onDark ? websiteLogoWhite : websiteLogo}
              alt="Sparkl"
              width={140}
              height={40}
              className="h-8 w-auto max-h-9 sm:h-9 object-contain object-left transition-opacity duration-300"
              decoding="async"
            />
          </a>

          {/* Desktop links */}
          <nav className="hidden lg:flex items-center gap-1">
            {LINKS.map((l) => (
              <a
                key={l.label}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`relative px-3.5 py-2 text-[13.5px] font-medium rounded-full transition-colors group ${
                  onDark
                    ? "text-slate-200 hover:text-white"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {l.label}
                <span className="absolute left-3.5 right-3.5 -bottom-0.5 h-px bg-gradient-to-r from-blue-500 to-violet-500 scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
              </a>
            ))}
          </nav>

          {/* CTAs */}
          <div className="hidden md:flex items-center gap-2">

            <button
              type="button"
              data-testid="nav-book-demo"
              onClick={() => openBookDemo()}
              className="btn-sheen inline-flex items-center gap-2 text-[13.5px] font-semibold text-white px-4 py-2 rounded-full shadow-[0_8px_20px_-8px_rgba(37,99,235,0.7)] hover:shadow-[0_10px_28px_-6px_rgba(37,99,235,0.75)] transition-all"
              style={{
                background:
                  "linear-gradient(135deg, #2563eb 0%, #4f46e5 50%, #7c3aed 100%)",
              }}
            >
              Book a demo
              <span className="text-white/80">→</span>
            </button>
          </div>

          {/* Mobile toggle */}
          <button
            className={`lg:hidden p-2 rounded-lg transition-colors ${
              onDark
                ? "text-white hover:bg-white/10"
                : "text-slate-700 hover:bg-slate-100"
            }`}
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle navigation"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div
            className={`lg:hidden pb-4 pt-2 border-t ${
              onDark ? "border-white/10" : "border-slate-200"
            }`}
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1">
              {LINKS.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  onClick={() => setMobileOpen(false)}
                  className={`px-3 py-2 text-sm font-medium rounded-lg ${
                    onDark
                      ? "text-slate-200 hover:bg-white/10"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {l.label}
                </a>
              ))}
              <div className="flex gap-2 mt-2">
    
                <button
                  type="button"
                  onClick={() => {
                    setMobileOpen(false);
                    openBookDemo();
                  }}
                  className="flex-1 text-center text-sm font-semibold text-white px-4 py-2 rounded-full"
                  style={{
                    background:
                      "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
                  }}
                >
                  Book a demo
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
